import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Sparkles, Loader2, ImageIcon, ImagePlus, Shield, Check, X, AlertTriangle, Lightbulb, CheckCircle2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';

const STANDALONE = '__standalone__';

function ReferenceUpload({ label, testid, valueId, onFile, onClear }) {
  const inputRef = useRef(null);
  const [name, setName] = useState('');
  return (
    <div>
      <Label className="text-[11px] text-muted-foreground">{label}</Label>
      <input ref={inputRef} type="file" accept="image/*" data-testid={testid} className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) { setName(f.name); onFile(f); } }} />
      {valueId ? (
        <div className="mt-1 flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1.5">
          <Check className="h-3 w-3 text-emerald-600 shrink-0" />
          <span className="text-[10px] text-emerald-700 truncate flex-1">{name || 'Added'}</span>
          <button type="button" data-testid={`${testid}-remove`} title="Remove"
            onClick={() => { setName(''); if (inputRef.current) inputRef.current.value = ''; onClear(); }}
            className="text-emerald-700 hover:text-red-600 transition-colors">
            <X className="h-3 w-3" />
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()}
          className="mt-1 w-full rounded-md border border-dashed border-[#E8E6E1] hover:border-[#B89B5E] px-2 py-2 text-[11px] text-stone-500 hover:text-[#B89B5E] flex items-center justify-center gap-1.5 transition-colors">
          <ImagePlus className="h-3.5 w-3.5" /> Add photo
        </button>
      )}
    </div>
  );
}

function SuccessVisualResult({ visual, onDeleted }) {
  const api = useApi();
  const [removing, setRemoving] = useState(false);
  const plan = visual.plan || {};
  const critic = visual.critic || {};
  const heroImages = visual.hero_images || {};
  const facts = plan.source_facts || [];
  const creative = plan.creative_elements || [];
  const passed = critic.passed !== false;
  const cost = visual.cost || {};
  const refsUsed = visual.references_used || [];

  const remove = async () => {
    if (!window.confirm('Delete this Success Visual? This cannot be undone.')) return;
    setRemoving(true);
    try {
      await api.del(`/media-studio/packs/${visual.id}`);
      onDeleted?.(visual.id);
      toast.success('Success Visual deleted');
    } catch (e) {
      toast.error(e.message || 'Delete failed');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Card className="bg-[#FAF9F7] border-[#E8E6E1]" data-testid={`sv-result-${visual.id}`}>
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <CardTitle className="text-base">{plan.concept || plan.headline || 'Success Visual'}</CardTitle>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <Badge variant="outline" className="text-[10px] uppercase">Success Visual</Badge>
              {passed
                ? <Badge variant="outline" className="text-[10px] border-emerald-200 text-emerald-700" data-testid={`sv-critic-passed-${visual.id}`}><Check className="h-3 w-3 mr-0.5" />Critic approved</Badge>
                : <Badge variant="outline" className="text-[10px] border-amber-200 text-amber-700" data-testid={`sv-critic-issues-${visual.id}`}><AlertTriangle className="h-3 w-3 mr-0.5" />Review suggested</Badge>}
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={remove} disabled={removing} data-testid={`sv-delete-${visual.id}`}>
            {removing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {Object.keys(heroImages).length > 0 && (
          <div className="flex flex-wrap gap-3" data-testid={`sv-hero-set-${visual.id}`}>
            {Object.entries(heroImages).map(([ratio, url]) => (
              <figure key={ratio} className="space-y-1">
                <img src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt={`Success visual ${ratio}`}
                  className="h-56 w-auto rounded-lg border border-[#E8E6E1] object-contain bg-white"
                  data-testid={`sv-hero-${ratio.replace(':', 'x')}-${visual.id}`} />
                <figcaption className="text-[10px] text-center text-muted-foreground uppercase tracking-wide">{ratio}</figcaption>
              </figure>
            ))}
          </div>
        )}

        {plan.headline && (
          <div className="text-sm">
            {plan.eyebrow && <p className="text-[11px] uppercase tracking-wide text-[#B89B5E] font-medium">{plan.eyebrow}</p>}
            <p className="font-semibold text-[#1A1A1A]">{plan.headline}</p>
            {plan.cta && <p className="text-sm font-medium text-[#B89B5E] mt-0.5">→ {plan.cta}</p>}
          </div>
        )}

        {/* Grounding transparency panel */}
        <div className="grid sm:grid-cols-2 gap-3" data-testid={`sv-grounding-${visual.id}`}>
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
            <div className="flex items-center gap-1.5 text-emerald-800 mb-1.5">
              <CheckCircle2 className="h-3.5 w-3.5" />
              <span className="text-xs font-semibold">Grounded in your data</span>
            </div>
            {facts.length > 0 ? (
              <ul className="space-y-1">
                {facts.map((f, i) => <li key={i} className="text-[11px] text-emerald-900/80 leading-snug">• {f}</li>)}
              </ul>
            ) : (
              <p className="text-[11px] text-emerald-900/60">No hard facts asserted — the scene is purely aspirational.</p>
            )}
          </div>
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
            <div className="flex items-center gap-1.5 text-amber-800 mb-1.5">
              <Lightbulb className="h-3.5 w-3.5" />
              <span className="text-xs font-semibold">Creative visualization</span>
            </div>
            {creative.length > 0 ? (
              <ul className="space-y-1">
                {creative.map((c, i) => <li key={i} className="text-[11px] text-amber-900/80 leading-snug">• {c}</li>)}
              </ul>
            ) : (
              <p className="text-[11px] text-amber-900/60">Illustrative only — not factual claims about the prospect.</p>
            )}
          </div>
        </div>

        {!passed && (critic.issues || []).length > 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3 text-[11px] text-amber-800" data-testid={`sv-critic-notes-${visual.id}`}>
            <span className="font-semibold">Critic notes:</span> {critic.issues.join(' · ')}
          </div>
        )}

        {(cost.image_calls != null || cost.llm_calls != null) && (
          <p className="text-[10px] text-muted-foreground" data-testid={`sv-cost-${visual.id}`}>
            Generated from 1 source scene · {cost.image_calls ?? '–'} image call{cost.image_calls === 1 ? '' : 's'} · {cost.llm_calls ?? '–'} text call{cost.llm_calls === 1 ? '' : 's'} · counts as 1 Success Visual
            {refsUsed.length > 0 && <span> · references: {refsUsed.join(', ')}</span>}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default function SuccessVisualGenerator({ opportunityId = null, defaultTopic = '', opportunityLabel = '', compact = false, onGenerated }) {
  const api = useApi();
  const locked = Boolean(opportunityId);
  const [opportunities, setOpportunities] = useState([]);
  const [selectedOpp, setSelectedOpp] = useState(opportunityId || STANDALONE);
  const [topic, setTopic] = useState(defaultTopic);
  const [logoUploadId, setLogoUploadId] = useState(null);
  const [personUploadId, setPersonUploadId] = useState(null);
  const [locationUploadId, setLocationUploadId] = useState(null);
  const [productUploadId, setProductUploadId] = useState(null);
  const [usage, setUsage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progressMsg, setProgressMsg] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const u = await api.get('/media-studio/success-visual/usage');
        setUsage(u);
      } catch (e) { /* silent */ }
      if (!locked) {
        try {
          const opps = await api.get('/opportunities');
          setOpportunities(Array.isArray(opps) ? opps : (opps?.opportunities || []));
        } catch (e) { /* silent */ }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const atCap = usage != null && usage.remaining <= 0;

  const uploadRef = async (file, setter, label) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', 'exact');
    fd.append('label', label);
    setUploading(true);
    try {
      const res = await api.upload('/media-studio/uploads', fd);
      setter(res.id);
      toast.success(`${label} added`);
    } catch (e) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const generate = async () => {
    if (atCap) { toast.error('Daily Success Visual limit reached'); return; }
    const oppId = selectedOpp === STANDALONE ? null : selectedOpp;
    if (!oppId && !topic.trim()) {
      toast.error('Pick an opportunity or add an angle to visualize');
      return;
    }
    setLoading(true);
    setResult(null);
    setProgressMsg('Planning your scene…');
    try {
      const res = await api.post('/media-studio/success-visual/generate', {
        opportunity_id: oppId,
        topic: topic.trim(),
        logo_upload_id: logoUploadId,
        person_upload_id: personUploadId,
        location_upload_id: locationUploadId,
        product_upload_id: productUploadId,
      });
      const jobId = res?.job_id;
      if (!jobId) throw new Error('Could not start generation');
      if (res?.usage) setUsage(res.usage);

      // Poll the async job — generation runs server-side for up to a few minutes,
      // well beyond the 60s request limit, so we never block a single request.
      const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
      const started = Date.now();
      const MAX_MS = 8 * 60 * 1000;
      let consecutiveErrors = 0;
      setProgressMsg('Painting your success scene… this can take a few minutes.');
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await sleep(3000);
        let job;
        try {
          job = await api.get(`/media-studio/success-visual/jobs/${jobId}`);
          consecutiveErrors = 0;
        } catch (e) {
          if (++consecutiveErrors >= 5) {
            throw new Error('Lost connection while generating — check My Packs shortly.');
          }
          continue;
        }
        if (job?.usage) setUsage(job.usage);
        if (job?.status === 'done') {
          setResult(job.success_visual);
          onGenerated?.(job.success_visual);
          toast.success('Success Visual generated');
          break;
        }
        if (job?.status === 'error') {
          throw new Error(job.error || 'Generation failed');
        }
        if (Date.now() - started > MAX_MS) {
          throw new Error('Generation is taking longer than expected — check My Packs shortly.');
        }
      }
    } catch (e) {
      const raw = e?.data?.detail;
      let msg = (raw && (raw.message || raw)) || e.message || 'Generation failed';
      if (typeof msg !== 'string' || /<!DOCTYPE|<html|cloudflare/i.test(msg)) {
        msg = 'Generation failed. Please try again in a moment.';
      }
      toast.error(msg);
    } finally {
      setLoading(false);
      setProgressMsg('');
    }
  };

  return (
    <div className="space-y-5">
      <Card className="bg-white/80 backdrop-blur border-[#E8E6E1]" data-testid="success-visual-generator">
        {!compact && (
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#B89B5E]/10"><ImageIcon className="h-5 w-5 text-[#B89B5E]" /></div>
              <div>
                <CardTitle className="text-lg">Success Visual</CardTitle>
                <p className="text-xs text-muted-foreground">An aspirational scene of your prospect thriving — grounded, never fabricated.</p>
              </div>
            </div>
          </CardHeader>
        )}
        <CardContent className="space-y-5 pt-5">
          {locked ? (
            <div className="rounded-md border border-[#E8E6E1] bg-[#FAF9F7] px-3 py-2 flex items-center gap-2" data-testid="sv-locked-opportunity">
              <Shield className="h-4 w-4 text-[#B89B5E]" />
              <div className="text-xs">
                <span className="text-muted-foreground">Grounded in opportunity: </span>
                <span className="font-medium text-[#1A1A1A]">{opportunityLabel || 'this opportunity'}</span>
              </div>
            </div>
          ) : (
            <div>
              <Label className="text-xs">Opportunity (optional — grounds the scene)</Label>
              <Select value={selectedOpp} onValueChange={setSelectedOpp}>
                <SelectTrigger data-testid="sv-opportunity-select"><SelectValue placeholder="Standalone (no opportunity)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={STANDALONE}>Standalone (no opportunity)</SelectItem>
                  {opportunities.map((o) => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.company_name || o.property_type || 'Opportunity'}{o.property_type ? ` — ${o.property_type}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label className="text-xs">Angle / message {selectedOpp !== STANDALONE && <span className="text-muted-foreground">(optional — opportunity context is used)</span>}</Label>
            <Textarea
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="What success story should the visual evoke?"
              rows={2}
              data-testid="sv-topic-input"
            />
          </div>

          <div className="rounded-md border border-[#E8E6E1] p-3 space-y-3" data-testid="sv-references">
            <div className="flex items-center gap-2">
              <ImagePlus className="h-4 w-4 text-[#B89B5E]" />
              <span className="text-xs font-medium text-[#1A1A1A]">Visual references (optional)</span>
            </div>
            <p className="text-[10px] text-muted-foreground -mt-1">
              Add real photos to personalize the aspirational scene. They're used as inspiration and fused into one cohesive scene — never a collage. All optional; leave blank to generate purely from intelligence.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              <ReferenceUpload label="Prospect / person" testid="sv-person-upload"
                valueId={personUploadId}
                onFile={(f) => uploadRef(f, setPersonUploadId, 'Person')}
                onClear={() => setPersonUploadId(null)} />
              <ReferenceUpload label="Prospect space / location" testid="sv-location-upload"
                valueId={locationUploadId}
                onFile={(f) => uploadRef(f, setLocationUploadId, 'Location')}
                onClear={() => setLocationUploadId(null)} />
              <ReferenceUpload label="Product / offer" testid="sv-product-upload"
                valueId={productUploadId}
                onFile={(f) => uploadRef(f, setProductUploadId, 'Product')}
                onClear={() => setProductUploadId(null)} />
              <ReferenceUpload label="Brand logo (composited exactly)" testid="sv-logo-upload"
                valueId={logoUploadId}
                onFile={(f) => uploadRef(f, setLogoUploadId, 'Logo')}
                onClear={() => setLogoUploadId(null)} />
            </div>
          </div>

          <div className="rounded-md border border-stone-200 bg-stone-50 p-3 text-[11px] text-stone-600 flex items-start gap-2">
            <Shield className="h-3.5 w-3.5 text-[#B89B5E] mt-0.5 shrink-0" />
            <span>The AI paints an aspirational scene; our art director adds the copy. No invented names, revenues, order sizes, or statistics are ever shown as fact.</span>
          </div>

          {atCap && (
            <div data-testid="sv-cap-message" className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              You've reached your daily limit of {usage.cap} Success Visuals. Your quota resets at midnight UTC.
            </div>
          )}

          <Button onClick={generate} disabled={loading || uploading || atCap} className="w-full bg-[#1A1A1A] hover:bg-[#2A2A2A] text-white disabled:opacity-60" data-testid="sv-generate-btn">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
            {loading ? 'Generating…' : 'Generate Success Visual'}
          </Button>

          {loading && progressMsg && (
            <div data-testid="sv-progress" className="rounded-md border border-[#E8E6E1] bg-[#FAF9F7] p-3 text-xs text-stone-600 flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-[#B89B5E]" />
              <span>{progressMsg} You can keep working — it will also appear under <span className="font-medium text-[#1A1A1A]">My Packs</span>.</span>
            </div>
          )}

          {usage && (
            <p data-testid="sv-usage-meter" className="text-center text-xs text-muted-foreground">
              <span data-testid="sv-usage-count">{usage.used_today} of {usage.cap} Success Visuals today</span> · resets at midnight UTC
            </p>
          )}
        </CardContent>
      </Card>

      {result && <SuccessVisualResult visual={result} onDeleted={() => setResult(null)} />}
    </div>
  );
}
